import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircleQuestion, Search, Filter, Clock, BookOpen,
  User, CheckCircle, AlertCircle, BarChart3, ArrowLeft, RefreshCw
} from "lucide-react";
import { API, useAuth } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import DoubtThread from "@/components/doubt/DoubtThread";

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtTs = (secs) => {
  if (!secs && secs !== 0) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const STATUS_COLORS = {
  pending:   { bg: "bg-[#FFCC00]/10", text: "text-[#FFCC00]", border: "border-[#FFCC00]/20" },
  in_review: { bg: "bg-[#00F0FF]/10", text: "text-[#00F0FF]", border: "border-[#00F0FF]/20" },
  answered:  { bg: "bg-[#00FF88]/10", text: "text-[#00FF88]", border: "border-[#00FF88]/20" },
  closed:    { bg: "bg-gray-100 dark:bg-white/5",      text: "text-[#52525B]", border: "border-white/5" },
};

const STATUS_LABELS = {
  pending: "Pending", in_review: "In Review", answered: "Answered", closed: "Closed",
};

// ── Student View ─────────────────────────────────────────────────────────────
function StudentView({ selectedDoubtId, setSelectedDoubtId }) {
  const [doubts, setDoubts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchDoubts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/doubts/my`, { withCredentials: true });
      setDoubts(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error("Failed to load your doubts"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDoubts(); }, [fetchDoubts]);

  if (selectedDoubtId) {
    return (
      <DoubtThread
        doubtId={selectedDoubtId}
        onBack={() => { setSelectedDoubtId(null); fetchDoubts(); }}
      />
    );
  }

  const filtered = filter === "all" ? doubts : doubts.filter((d) => d.status === filter);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", value: doubts.length, color: "text-white" },
          { label: "Pending", value: doubts.filter((d) => d.status === "pending").length, color: "text-[#FFCC00]" },
          { label: "In Review", value: doubts.filter((d) => d.status === "in_review").length, color: "text-[#00F0FF]" },
          { label: "Answered", value: doubts.filter((d) => d.status === "answered").length, color: "text-[#00FF88]" },
        ].map((s, i) => (
          <div key={i} className="bg-white dark:bg-[#131B2C] border border-gray-200 dark:border-white/[0.06] rounded-xl p-4 text-center">
            <p className="text-[10px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase">{s.label}</p>
            <p className={`text-2xl font-bold font-outfit mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {["all", "pending", "in_review", "answered", "closed"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs font-jetbrains px-3 py-1.5 rounded-lg transition-all capitalize ${
              filter === f
                ? "bg-[#A855F7]/20 text-[#A855F7] font-bold"
                : "bg-gray-100 dark:bg-white/5 text-[#A1A1AA] hover:text-gray-900 dark:text-white hover:bg-white/10"
            }`}
          >
            {f.replace("_", " ")}
          </button>
        ))}
        <button onClick={fetchDoubts} className="ml-auto text-gray-400 dark:text-[#52525B] hover:text-gray-900 dark:text-white transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Doubt cards */}
      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-2 border-[#A855F7] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center bg-gray-50 dark:bg-white/[0.02] border border-dashed border-gray-200 dark:border-white/10 rounded-xl">
          <MessageCircleQuestion className="w-10 h-10 text-gray-400 dark:text-[#52525B] mx-auto mb-3" />
          <p className="text-gray-500 dark:text-[#A1A1AA]">{filter === "all" ? "No doubts yet. Ask your first question from a lesson page!" : `No ${filter.replace("_", " ")} doubts.`}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((d) => {
            const sc = STATUS_COLORS[d.status] || STATUS_COLORS.pending;
            return (
              <div
                key={d.doubt_id}
                onClick={() => setSelectedDoubtId(d.doubt_id)}
                className="bg-white dark:bg-[#131B2C] border border-gray-200 dark:border-white/[0.06] rounded-xl p-4 cursor-pointer hover:border-[#A855F7]/30 hover:bg-[#A855F7]/[0.03] transition-all"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-gray-900 dark:text-white text-sm font-medium flex-1 leading-snug line-clamp-2">{d.doubt_text}</p>
                  <Badge className={`border-0 text-[10px] font-jetbrains flex-shrink-0 ${sc.bg} ${sc.text}`}>
                    {STATUS_LABELS[d.status] || d.status}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">
                  {d.lesson_name && (
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-3 h-3" /> {d.lesson_name}
                    </span>
                  )}
                  {d.timestamp_in_video > 0 && (
                    <span className="flex items-center gap-1 text-[#FFCC00]">
                      <Clock className="w-3 h-3" /> {fmtTs(d.timestamp_in_video)}
                    </span>
                  )}
                  <span className="ml-auto">{fmtDate(d.created_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Staff View (Admin / Mentor) ───────────────────────────────────────────────
function StaffView({ selectedDoubtId, setSelectedDoubtId }) {
  const [doubts, setDoubts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;

      const [dRes, sRes] = await Promise.all([
        axios.get(`${API}/doubts`, { params, withCredentials: true }),
        axios.get(`${API}/doubts/stats`, { withCredentials: true }),
      ]);
      setDoubts(Array.isArray(dRes.data) ? dRes.data : []);
      setStats(sRes.data);
    } catch { toast.error("Failed to load doubts"); }
    finally { setLoading(false); }
  }, [statusFilter, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (selectedDoubtId) {
    return (
      <DoubtThread
        doubtId={selectedDoubtId}
        onBack={() => { setSelectedDoubtId(null); fetchAll(); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: "Total", value: stats.total, color: "text-white" },
            { label: "Pending", value: stats.pending, color: "text-[#FFCC00]" },
            { label: "In Review", value: stats.in_review, color: "text-[#00F0FF]" },
            { label: "Answered", value: stats.answered, color: "text-[#00FF88]" },
            { label: "Closed", value: stats.closed, color: "text-[#52525B]" },
          ].map((s, i) => (
            <div key={i} className="bg-white dark:bg-[#131B2C] border border-gray-200 dark:border-white/[0.06] rounded-xl p-4 text-center">
              <p className="text-[10px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase">{s.label}</p>
              <p className={`text-2xl font-bold font-outfit mt-1 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-[#52525B]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search doubts, students, lessons..."
            className="pl-9 bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white placeholder-[#52525B]"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "pending", "in_review", "answered", "closed"].map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`text-xs font-jetbrains px-3 py-1.5 rounded-lg transition-all capitalize ${
                statusFilter === f
                  ? "bg-[#A855F7]/20 text-[#A855F7] font-bold"
                  : "bg-gray-100 dark:bg-white/5 text-[#A1A1AA] hover:text-gray-900 dark:text-white"
              }`}
            >
              {f.replace("_", " ")}
            </button>
          ))}
          <button onClick={fetchAll} className="text-gray-400 dark:text-[#52525B] hover:text-gray-900 dark:text-white transition-colors p-1.5">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-2 border-[#A855F7] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : doubts.length === 0 ? (
        <div className="py-16 text-center bg-gray-50 dark:bg-white/[0.02] border border-dashed border-gray-200 dark:border-white/10 rounded-xl">
          <MessageCircleQuestion className="w-10 h-10 text-gray-400 dark:text-[#52525B] mx-auto mb-3" />
          <p className="text-gray-500 dark:text-[#A1A1AA]">No doubts found</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#131B2C] border border-gray-200 dark:border-white/[0.06] rounded-xl overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-[2fr_1.5fr_1fr_0.8fr_0.8fr_auto] gap-4 px-5 py-3 bg-gray-50 dark:bg-white/[0.02] border-b border-gray-200 dark:border-white/[0.06]">
            {["Student", "Lesson", "Timestamp", "Status", "Asked", "Action"].map((h) => (
              <span key={h} className="text-[10px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase">{h}</span>
            ))}
          </div>

          <div className="divide-y divide-white/[0.04]">
            {doubts.map((d) => {
              const sc = STATUS_COLORS[d.status] || STATUS_COLORS.pending;
              return (
                <div
                  key={d.doubt_id}
                  className="grid md:grid-cols-[2fr_1.5fr_1fr_0.8fr_0.8fr_auto] gap-4 px-5 py-4 items-center hover:bg-gray-50 dark:bg-white/[0.02] transition-colors"
                >
                  {/* Student */}
                  <div>
                    <p className="text-sm text-gray-900 dark:text-white font-medium">{d.student_name}</p>
                    <p className="text-xs text-gray-400 dark:text-[#52525B] truncate mt-0.5">{d.doubt_text}</p>
                  </div>

                  {/* Lesson */}
                  <div>
                    <p className="text-xs text-[#CBD5E1] truncate">{d.lesson_name || "—"}</p>
                    {d.course_name && <p className="text-[10px] text-gray-400 dark:text-[#52525B] truncate">{d.course_name}</p>}
                  </div>

                  {/* Timestamp */}
                  <span className="text-xs font-jetbrains text-[#FFCC00]">
                    {d.timestamp_in_video > 0 ? fmtTs(d.timestamp_in_video) : "—"}
                  </span>

                  {/* Status */}
                  <Badge className={`border-0 text-[10px] font-jetbrains w-fit ${sc.bg} ${sc.text}`}>
                    {STATUS_LABELS[d.status] || d.status}
                  </Badge>

                  {/* Date */}
                  <span className="text-xs text-gray-400 dark:text-[#52525B] hidden md:block">{fmtDate(d.created_at)}</span>

                  {/* Action */}
                  <Button
                    size="sm"
                    onClick={() => setSelectedDoubtId(d.doubt_id)}
                    className="bg-[#A855F7]/10 text-[#A855F7] hover:bg-[#A855F7] hover:text-gray-900 dark:text-white border-0 h-7 text-xs px-3"
                  >
                    View
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DoubtCentrePage() {
  const { user } = useAuth();
  const [selectedDoubtId, setSelectedDoubtId] = useState(null);
  const isStaff = user?.role === "admin" || user?.role === "mentor";

  return (
    <DashboardLayout title="Doubt Centre">
      <div className="space-y-6" data-testid="doubt-centre-page">
        {/* Page Header */}
        {!selectedDoubtId && (
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-outfit font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#A855F7]/10 flex items-center justify-center">
                  <MessageCircleQuestion className="w-5 h-5 text-[#A855F7]" />
                </div>
                Doubt Centre
              </h1>
              <p className="text-sm text-gray-500 dark:text-[#A1A1AA] mt-1 ml-12">
                {isStaff
                  ? "Manage and respond to student doubts"
                  : "View and track your submitted doubts"}
              </p>
            </div>
          </div>
        )}

        {isStaff ? (
          <StaffView
            selectedDoubtId={selectedDoubtId}
            setSelectedDoubtId={setSelectedDoubtId}
          />
        ) : (
          <StudentView
            selectedDoubtId={selectedDoubtId}
            setSelectedDoubtId={setSelectedDoubtId}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
